import { Location, type VehicleGroup } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { usePermissions } from '@cobliteam/permissions';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Placeholder from '@cobliteam/ui/components/Placeholder';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Label from '@cobliteam/ui/form/Label';
import type React from 'react';
import { useState } from 'react';

export interface MultiSelectVehicleGroupsOverlayProps {
  isLoading?: boolean;
  vehicleGroups: VehicleGroup[];
  selectedGroups: VehicleGroup[];
  selectionChanged: boolean;
  onChange: (group: VehicleGroup) => void;
  onClearFilter: () => void;
  onApplyFilter: () => void;
  onCancel: () => void;
}

const MultiSelectVehicleGroupsOverlay: React.FC<MultiSelectVehicleGroupsOverlayProps> = ({
  isLoading,
  selectedGroups,
  vehicleGroups,
  selectionChanged,
  onChange,
  onClearFilter,
  onApplyFilter,
  onCancel,
}) => {
  const [searchBoxInput, setSearchBoxInput] = useState('');
  const [selectedGroupsDraft] = useState<VehicleGroup[]>(selectedGroups);

  const { t } = useTranslation('multiselects');
  const { isAccessAllowed } = usePermissions();
  const hasUpdateVehiclePermission = isAccessAllowed('create-update-delete:vehicle');

  const handleCreateGroup = () => {
    window.open(`${window.location.origin}${Location.VEHICLES}`, '_blank')!.focus();
  };

  const renderNoGroupsPlaceholder = () => (
    <div>
      <div className='m-7 flex flex-wrap items-start justify-center gap-3'>
        <Placeholder
          className='m-0'
          icon='cb-placeholder-vehicle-group'
          text={hasUpdateVehiclePermission ? t('groups.no_groups_with_permission_message') : t('groups.no_groups_without_permission_message')}
        />
        {hasUpdateVehiclePermission && <Button onClick={handleCreateGroup}>{t('groups.create_groups')}</Button>}
      </div>
    </div>
  );

  const sortByName = (a: VehicleGroup, b: VehicleGroup) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  };

  const sortBySelected = (a: VehicleGroup, b: VehicleGroup) => {
    if (selectedGroupsDraft.find((g) => g.id === a.id) && !selectedGroupsDraft.find((g) => g.id === b.id)) return -1;
    if (!selectedGroupsDraft.find((g) => g.id === a.id) && selectedGroupsDraft.find((g) => g.id === b.id)) return 1;
    return 0;
  };

  const filterOptionsBySearchBoxInput = () => {
    const normalizedSearchBoxInput = searchBoxInput.trim().toLowerCase();
    if (normalizedSearchBoxInput.length > 0) {
      return vehicleGroups.filter((group) => group.name.toLocaleLowerCase().match(normalizedSearchBoxInput));
    }
    return [...vehicleGroups];
  };

  const filteredOptions = filterOptionsBySearchBoxInput();

  const groupsFiltered: VehicleGroup[] = filteredOptions.sort(sortByName).sort(sortBySelected);

  return (
    <Dropdown.Overlay>
      <Dropdown.Header className='px-2'>
        <Button variant='ghost' size='sm' disabled={selectedGroups.length === 0} onClick={onClearFilter}>
          {t('groups.clear_selection')}
        </Button>
      </Dropdown.Header>
      <Dropdown.Search className='px-2'>
        <InputSearch autoFocus placeholder={t('groups.search_placeholder')} className='w-full' onChange={setSearchBoxInput} />
      </Dropdown.Search>
      <Dropdown.Body className='pl-5'>
        <Dropdown.List
          heightList={300}
          heightItem={28}
          data={groupsFiltered}
          isLoading={isLoading}
          placeholderComponent={renderNoGroupsPlaceholder()}
          loadingComponent={<Dropdown.Loading />}
          itemComponent={(group: VehicleGroup) => {
            const disabled = group.vehicleIds && group.vehicleIds.length === 0;
            const checkbox = (
              <div className='flex items-center gap-2'>
                <Checkbox
                  id={group.name}
                  data-testid={`vehicle-group-filter-${group.id}`}
                  data-cy={`vehicle-group-filter-${group.id}`}
                  disabled={disabled}
                  checked={selectedGroups.some((selectedGroup) => selectedGroup.id === group.id)}
                  onCheckedChange={() => onChange(group)}
                  value={group.name}
                  title={group.name}
                />
                <Label htmlFor={group.name} className='line-clamp-1 text-start' title={group.name}>
                  {group.name}
                </Label>
              </div>
            );

            return (
              <div key={group.name} className='line-clamp-1 w-full pr-4'>
                <Tooltip enable={disabled} content={t('groups.group_without_vehicles')}>
                  {checkbox}
                </Tooltip>
              </div>
            );
          }}
        />
      </Dropdown.Body>
      <Dropdown.Footer>
        <Dropdown.Actions className='px-2'>
          <Button variant='ghost' full size='sm' onClick={onCancel}>
            {t('groups.cancel')}
          </Button>
          <Button size='sm' full onClick={onApplyFilter} disabled={vehicleGroups.length === 0 || !selectionChanged}>
            {t('groups.apply')}
          </Button>
        </Dropdown.Actions>
      </Dropdown.Footer>
    </Dropdown.Overlay>
  );
};

export default MultiSelectVehicleGroupsOverlay;
